import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ButtonBase } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import useIsMountedRef from 'use-is-mounted-ref'
import { useFormContext, UseFormMethods, useWatch } from '../../../Vendor/react-hook-form/dist/index.ie11'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState, UserInfoContext } from '../../Common/Contexts'
import { ApiKeys } from '../../../../i18n'
import { API_KEY_EXPIRY_FIELD_NAME, API_KEY_NAME_FIELD_NAME, API_KEYS_FIELD_NAME } from '../FieldNames'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import Icon, { IconName } from '../../Common/Components/Icon'
import { APIKeyForm, APIKeysInfo, APIKeyStatus, APIToken, ApplicationUserFormData } from '../Types'
import { StateAppender, useStateAppender } from '../../Common/Hooks/useStateAppender'
import { EMPTY_STRING, UNDERSCORE } from '../../Common/Constants/Separators'
import { ValidationErrors } from '../../Common/Components/ValidationErrors/ValidationErrors'
import PromptContext, { PromptDisplayType } from '../../Common/Contexts/PromptContext'
import { $debug } from '../../Common/Utils/ConsoleUtils'
import { displayError, displayErrorMessage, displaySuccessMessage } from '../../Common/Utils/Messages'
import { isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { DatePickerForms } from '../../Common/Components/DateTimePicker/DateTimePickerForms'
import { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { DASH } from '../../../NonReact/constants/Common'
import Loader from '../../Common/Components/Loader'
import { createAPIKeyForUser, deleteAPIKeyForUser, getAPIKeysForUser } from '../../Common/API/APIKeysClient'
import { JSONResponse } from '../../../NonReact/common/Types/Types'
import { OutlinedTextInput } from '../../../ReactRouting/Components/Atoms/Inputs/OutlinedTextInput'

type Props = {
    viewOnly?: boolean
    selectedUser: Nullable<string>
}

const MAX_API_KEYS: number = 5

export const APIKeys: FC<Props> = ({ selectedUser, viewOnly }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(ApiKeys.NAME)

    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)
    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const { setValue, trigger }: UseFormMethods<ApplicationUserFormData> = useFormContext()

    const {
        state: apiKeys,
        appender: apiKeyAppender,
        setter: setAPIKeys
    }: StateAppender<APIKeysInfo> = useStateAppender<APIKeysInfo>([])
    const [isAdding, setIsAdding]: ReactState<boolean> = useState<boolean>(false)
    const [apiToken, setAPIToken]: ReactState<Nullable<APIToken>> = useState<Nullable<APIToken>>()
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(true)
    const [isAddSubmitting, setIsAddSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const {
        state: removedAPIKeys,
        appender: appendRemovedAPIKey,
        remover: removeRemovedAPIKey
    }: StateAppender<Nullable<string>> = useStateAppender<Nullable<string>>([])

    const addedAPIKey: Definable<APIKeyForm> = useWatch({ name: API_KEYS_FIELD_NAME })

    const dateLocale: string = useMemo(
        (): string => userInfo?.SessionInfo?.Language?.replace(UNDERSCORE, DASH) ?? 'en-GB',
        [userInfo]
    )

    const cancelAdding = useCallback((): void => {
        setIsAdding(false)
        setIsAddSubmitting(false)

        setValue(API_KEYS_FIELD_NAME, {
            name: EMPTY_STRING,
            expiry: null
        })

        trigger([API_KEY_NAME_FIELD_NAME, API_KEY_EXPIRY_FIELD_NAME])
    }, [])

    const addAPIKey = useCallback((): void => {
        if (!isAddSubmitting) {
            setIsAddSubmitting(true)
            trigger([API_KEY_NAME_FIELD_NAME, API_KEY_EXPIRY_FIELD_NAME]).then((isValid: boolean): void => {
                if (!isValid) {
                    return
                }

                if (isVarNotEmpty(addedAPIKey) && isStringNotEmpty(selectedUser)) {
                    createAPIKeyForUser(selectedUser, {
                        Name: addedAPIKey.name ?? EMPTY_STRING,
                        DateTimeExpiry: addedAPIKey.expiry?.valueOf()
                    })
                        .then((response: Nullable<APIKeysInfo>): void => {
                            if (isMounted.current && isVarNotEmpty(response)) {
                                apiKeyAppender(response)
                                setAPIToken({
                                    id: response.id,
                                    name: response.name,
                                    token: response.apiKey
                                })
                            }
                        })
                        .catch(displayError)
                        .finally((): void => {
                            if (isMounted.current) {
                                cancelAdding()
                            }
                        })
                }
            })
        }
    }, [addedAPIKey, isAddSubmitting])

    const revokeAPIKey = useCallback(
        (id?: string): Promise<void> =>
            displayPromptPromise(
                language.get(ApiKeys.ApiKeys_Revoke_ConfirmMessage),
                language.get(ApiKeys.ApiKeys_Revoke_Confirm),
                language.get(ApiKeys.ApiKeys_Revoke_Cancel)
            )
                .then((): void => {
                    if (isStringNotEmpty(id) && isStringNotEmpty(selectedUser)) {
                        appendRemovedAPIKey(id)
                        deleteAPIKeyForUser(selectedUser, id)
                            .then((): void => {
                                if (isMounted.current) {
                                    removeRemovedAPIKey(id)
                                    setAPIKeys((currentAPIKeys: APIKeysInfo[]): APIKeysInfo[] =>
                                        currentAPIKeys.filter((apiKey: APIKeysInfo): boolean => apiKey.id !== id)
                                    )

                                    if (apiToken?.id === id) {
                                        setAPIToken(undefined)
                                    }
                                }
                            })
                            .catch(displayError)
                    }
                })
                .catch((): void => $debug('Revoke API key cancelled')),
        [selectedUser, apiToken]
    )

    const handleEnter = useCallback(
        (event: React.KeyboardEvent): void => {
            event.preventDefault()
            addAPIKey()
        },
        [addedAPIKey, isAddSubmitting]
    )

    useEffect((): void => {
        if (isStringNotEmpty(selectedUser)) {
            getAPIKeysForUser(selectedUser)
                .then((apiKeysResponse: JSONResponse<APIKeysInfo[]>): void => {
                    if (apiKeysResponse && isMounted.current) {
                        setAPIKeys(apiKeysResponse)
                    }
                })
                .catch(displayError)
                .finally((): void => {
                    if (isMounted.current) {
                        setIsLoading(false)
                    }
                })
        }
    }, [])

    return isLoading ? (
        <div className='user-api-keys-loader-container'>
            <Loader size={40} />
        </div>
    ) : (
        <div className='user-api-keys'>
            {isStringNotEmpty(selectedUser) ? (
                <>
                    <table className='uak-table'>
                        <thead>
                            <tr className='uak-table-main-heading'>
                                <th colSpan={5}>{language.get(ApiKeys.ApiKeys_TableHeader_Title)}</th>
                            </tr>
                            <tr className='uak-table-secondary-heading'>
                                <th>{language.get(ApiKeys.ApiKeys_TableHeader_Name)}</th>
                                <th>{language.get(ApiKeys.ApiKeys_TableHeader_Expiry)}</th>
                                <th>{language.get(ApiKeys.ApiKeys_TableHeader_Created)}</th>
                                <th colSpan={2}>{language.get(ApiKeys.ApiKeys_TableHeader_LastAccessed)}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {apiKeys.map(
                                (
                                    {
                                        id,
                                        name,
                                        dateTimeExpiry,
                                        dateTimeCreated,
                                        dateTimeLastUsed,
                                        status
                                    }: APIKeysInfo,
                                    index: number
                                ): ReactElement => {
                                    let expiry: string = dateTimeExpiry
                                        ? new Date(dateTimeExpiry).toLocaleDateString(dateLocale)
                                        : language.get(ApiKeys.ApiKeys_Indefinite)

                                    if (status === APIKeyStatus.EXPIRED) {
                                        expiry = language.get(ApiKeys.ApiKeys_Expired)
                                    }

                                    return (
                                        <tr key={index}>
                                            <td>{name}</td>
                                            <td>{expiry}</td>
                                            <td>
                                                {isVarNotEmpty(dateTimeCreated)
                                                    ? new Date(dateTimeCreated).toLocaleDateString(dateLocale)
                                                    : EMPTY_STRING}
                                            </td>
                                            <td>
                                                {isVarNotEmpty(dateTimeLastUsed)
                                                    ? new Date(dateTimeLastUsed).toLocaleDateString(dateLocale)
                                                    : language.get(ApiKeys.ApiKeys_Indefinite)}
                                            </td>
                                            <td className='uak-revoke-container'>
                                                {isStringNotEmpty(id) && removedAPIKeys.includes(id) ? (
                                                    <Loader size={16} />
                                                ) : (
                                                    <ButtonBase
                                                        className='uak-revoke-button'
                                                        onClick={(): void => {
                                                            revokeAPIKey(id)
                                                        }}
                                                    >
                                                        {status === APIKeyStatus.ACTIVE
                                                            ? language.get(ApiKeys.ApiKeys_RevokeButton)
                                                            : language.get(ApiKeys.ApiKeys_RemoveButton)}
                                                    </ButtonBase>
                                                )}
                                            </td>
                                        </tr>
                                    )
                                }
                            )}
                            {isAdding ? (
                                <tr className='uak-add-key-container'>
                                    <td>
                                        <ValidationErrors name={API_KEY_NAME_FIELD_NAME}>
                                            <OutlinedTextInput
                                                onEnter={handleEnter}
                                                inputName={API_KEY_NAME_FIELD_NAME}
                                                placeholder={language.get(ApiKeys.ApiKeys_Placeholder_Description)}
                                                rules={{
                                                    required: language.get(ApiKeys.ApiKeys_Validations_Required),
                                                    maxLength: {
                                                        value: 50,
                                                        message: language.get(
                                                            ApiKeys.ApiKeys_Name_Validations_MaxLength
                                                        )
                                                    }
                                                }}
                                            />
                                        </ValidationErrors>
                                    </td>
                                    <td colSpan={2}>
                                        <ValidationErrors name={API_KEY_EXPIRY_FIELD_NAME}>
                                            <DatePickerForms
                                                onEnter={handleEnter}
                                                name={API_KEY_EXPIRY_FIELD_NAME}
                                                placeholder={language.get(ApiKeys.ApiKeys_Indefinite)}
                                                clearable={true}
                                                disablePast={true}
                                            />
                                        </ValidationErrors>
                                    </td>
                                    <td className='uak-add-buttons' colSpan={2}>
                                        {isAddSubmitting ? (
                                            <Loader className='uak-add-loading' size={20} />
                                        ) : (
                                            <div>
                                                <IconButton
                                                    iconName={IconName.CHECK}
                                                    type={IconButtonType.BUTTON}
                                                    onClick={addAPIKey}
                                                />
                                                <IconButton
                                                    iconName={IconName.X}
                                                    type={IconButtonType.BUTTON}
                                                    onClick={cancelAdding}
                                                />
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ) : (
                                <></>
                            )}
                        </tbody>
                    </table>
                    {apiKeys.length >= MAX_API_KEYS ? (
                        <Alert severity='warning'>{language.get(ApiKeys.ApiKeys_MaxAPIKeysReached)}</Alert>
                    ) : (
                        <></>
                    )}
                    {!isAdding && apiKeys.length < MAX_API_KEYS && !viewOnly ? (
                        <ButtonBase
                            className='uak-add-button'
                            type='button'
                            onClick={(): void => {
                                setAPIToken(undefined)
                                setIsAdding(true)
                            }}
                        >
                            {language.get(ApiKeys.ApiKeys_Add_New)}
                        </ButtonBase>
                    ) : (
                        <></>
                    )}
                    {isVarNotEmpty(apiToken) ? (
                        <div className='uak-api-token-container'>
                            <h3 className='uak-api-token-title'>
                                {language.getInterpolated(ApiKeys.ApiKeys_KeyDisplay_Title, apiToken.name)}
                            </h3>
                            <div className='uak-api-token-subtitle'>
                                {language.get(ApiKeys.ApiKeys_KeyDisplay_SubTitle)}
                            </div>
                            <ButtonBase
                                className='uak-api-token-copy-button'
                                onClick={(): void => {
                                    window.navigator.clipboard
                                        .writeText(apiToken.token ?? EMPTY_STRING)
                                        .then((): void => {
                                            displaySuccessMessage(language.get(ApiKeys.ApiKeys_Copy_Success))
                                        })
                                        .catch((): void => {
                                            displayErrorMessage(language.get(ApiKeys.ApiKeys_Copy_Fail))
                                        })
                                }}
                            >
                                <div className='uak-api-token-copy-container'>
                                    <div className='uak-api-token-copy'>{apiToken.token}</div>
                                    <Icon iconName={IconName.COPY} />
                                </div>
                            </ButtonBase>
                        </div>
                    ) : (
                        <></>
                    )}
                </>
            ) : (
                language.get(ApiKeys.ApiKeys_AddMode)
            )}
        </div>
    )
}
